<script setup>
// Налаштування глядача у власному чаті ефіру: свій нік, колір ніка і значок
// біля імені — той самий значок, що на Twitch стоїть перед ніком.
//
// Панель відкривається поверх стрічки, а не окремою сторінкою: міняють
// нік і колір рівно тоді, коли дивляться на чат, і йти заради цього в
// профіль сайту — це піти з ефіру.
//
// Нік — окреме поле, а не перейменування акаунта: входять на сайт через
// Twitch чи Discord, і чіпати логін заради підпису в чаті не можна. У чаті
// показується саме цей нік, за ним же працюють згадки «@».
//
// Колір вибирається зі списку, а не піпеткою: чат завжди на темному тлі, і
// половина довільних hex-кольорів на ньому просто не читається. Те саме
// правило стоїть і на сервері — інакше палітра означала б тільки те, що
// піпетку прибрали з екрана.

const props = defineProps({
  // Профіль із чату (кадр hello): чинні нік, колір і значок
  me: { type: Object, required: true }
})

// saved — оновлений профіль із сервера: чат має підхопити його одразу, не
// чекаючи, доки протухне профіль на зʼєднанні
const emit = defineEmits(['saved', 'close'])

const { save, uploadBadge, clearBadge } = useChatProfile()

const media = useStrapiMedia

// Нік, який показує чат, коли свого немає: твічевий логін. Його ж і
// підставляємо підказкою в порожнє поле
const fallbackName = computed(() => props.me.defaultName || props.me.name)

const name = ref(props.me.chatName || '')
const color = ref(props.me.color || nickColor(props.me.name))
// Колір «як було» — тобто той, що рахується з імені. Поки його не чіпали,
// не надсилаємо взагалі: інакше перше ж збереження зафіксувало б
// випадковий колір назавжди
const colorTouched = ref(!!props.me.color)

const badge = ref(props.me.badge || null)
const badgeInput = ref(null)

const busy = ref(false)
const failure = ref('')
const done = ref(false)

// Як це виглядатиме в чаті. Показуємо саме рядок чату, а не абстрактний
// кружечок: колір ніка перевіряють очима, і перевіряти його треба на тому
// самому фоні
const previewName = computed(() => name.value.trim() || fallbackName.value)
const previewColor = computed(() =>
  colorTouched.value ? color.value : nickColor(previewName.value)
)

const pickColor = (value) => {
  color.value = value
  colorTouched.value = true
}

const resetColor = () => {
  colorTouched.value = false
  color.value = nickColor(previewName.value)
}

/**
 * Одна кнопка «Зберегти» на нік і колір: це одне налаштування в очах
 * людини, і два окремі збереження тут виглядали б як помилка верстки
 */
const submit = async () => {
  busy.value = true
  failure.value = ''
  done.value = false

  try {
    const profile = await save({
      name: name.value.trim(),
      // Порожній рядок повертає колір за замовчуванням — той, що
      // рахується з імені
      color: colorTouched.value ? color.value : ''
    })

    badge.value = profile.badge || null
    name.value = profile.chatName || ''
    colorTouched.value = !!profile.color
    color.value = profile.color || nickColor(profile.name)
    done.value = true

    emit('saved', profile)
  } catch (e) {
    failure.value = e.message
  } finally {
    busy.value = false
  }
}

const pickBadge = () => badgeInput.value?.click()

const onBadge = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return

  busy.value = true
  failure.value = ''
  done.value = false

  try {
    const profile = await uploadBadge(file)
    badge.value = profile.badge || null
    done.value = true
    emit('saved', profile)
  } catch (e) {
    failure.value = e.message
  } finally {
    busy.value = false
  }
}

const dropBadge = async () => {
  busy.value = true
  failure.value = ''

  try {
    const profile = await clearBadge()
    badge.value = null
    emit('saved', profile)
  } catch (e) {
    failure.value = e.message
  } finally {
    busy.value = false
  }
}

// Escape закриває панель — вона поверх стрічки, і читати чат крізь неї
// однаково не вийде
const onKey = (event) => {
  if (event.key === 'Escape') emit('close')
}

onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <div class="z-30 absolute inset-0 flex flex-col bg-base-300">
    <div
      class="flex flex-shrink-0 items-center gap-2 bg-base-200 px-2 py-1.5 border-base-content/10 border-b"
    >
      <span class="font-semibold text-xs">Мій профіль у чаті</span>
      <button
        class="ml-auto text-base-content/50 hover:text-base-content"
        title="Закрити (Esc)"
        @click="emit('close')"
      >
        ✕
      </button>
    </div>

    <div class="flex flex-col gap-3 p-3 overflow-y-auto grow">
      <!-- Як це виглядатиме в стрічці -->
      <div class="bg-base-200 px-2 py-1.5 rounded text-sm leading-snug">
        <span class="mr-1 text-[10px] text-base-content/35 tabular-nums">00:00</span>
        <img
          v-if="badge"
          :src="media(badge)"
          class="inline-block align-middle mr-1 w-4 h-4 object-contain"
          alt=""
        />
        <span
          class="font-semibold"
          :style="{ color: previewColor }"
          >{{ previewName }}</span
        ><span class="mr-1 text-base-content/50">:</span>
        <span class="text-base-content/80">так вас бачитимуть у чаті</span>
      </div>

      <!-- Нік -->
      <label class="flex flex-col gap-1">
        <span class="text-[11px] text-base-content/60">Нік у чаті</span>
        <input
          v-model="name"
          type="text"
          maxlength="25"
          :placeholder="fallbackName"
          class="input-bordered w-full h-8 text-sm input input-sm"
        />
        <span class="text-[10px] text-base-content/40">
          2–25 символів: латиниця, цифри, «_», «.», «-». Порожньо — показуємо
          {{ fallbackName }}
        </span>
      </label>

      <!-- Колір -->
      <div class="flex flex-col gap-1.5">
        <span class="text-[11px] text-base-content/60">Колір ніка</span>

        <div class="flex flex-wrap gap-1">
          <button
            v-for="option in NICK_COLORS"
            :key="option"
            type="button"
            class="rounded w-5 h-5 transition-transform"
            :class="
              colorTouched && color.toLowerCase() === option.toLowerCase()
                ? 'ring-2 ring-base-content scale-110'
                : 'hover:scale-110'
            "
            :style="{ backgroundColor: option }"
            :title="option"
            @click="pickColor(option)"
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="text-[10px] text-base-content/40">{{
            colorTouched ? 'свій колір' : 'колір за іменем'
          }}</span>
          <button
            v-if="colorTouched"
            class="ml-auto btn btn-ghost btn-xs"
            @click="resetColor"
          >
            Скинути
          </button>
        </div>
      </div>

      <!-- Значок -->
      <div class="flex flex-col gap-1.5">
        <span class="text-[11px] text-base-content/60">Значок біля ніка</span>

        <div class="flex items-center gap-2">
          <div
            class="flex justify-center items-center bg-base-200 border border-base-content/10 rounded w-9 h-9 shrink-0"
          >
            <img
              v-if="badge"
              :src="media(badge)"
              class="max-w-7 max-h-7 object-contain"
              alt=""
            />
            <span
              v-else
              class="text-[9px] text-base-content/35"
              >немає</span
            >
          </div>

          <button
            class="btn btn-xs"
            :disabled="busy"
            @click="pickBadge"
          >
            {{ badge ? 'Замінити' : 'Завантажити' }}
          </button>

          <button
            v-if="badge"
            class="btn btn-ghost btn-xs text-error"
            :disabled="busy"
            @click="dropBadge"
          >
            Прибрати
          </button>
        </div>

        <input
          ref="badgeInput"
          type="file"
          class="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
          @change="onBadge"
        />

        <span class="text-[10px] text-base-content/40">
          Квадратна картинка до 256 КБ. У чаті вона показується розміром із
          рядок тексту, тож дрібні деталі на ній не читатимуться
        </span>
      </div>

      <p
        v-if="failure"
        class="text-[11px] text-error"
      >
        {{ failure }}
      </p>
      <p
        v-else-if="done"
        class="text-[11px] text-success"
      >
        Збережено — нові повідомлення підуть уже так
      </p>
    </div>

    <div class="flex flex-shrink-0 gap-2 bg-base-200 p-2 border-base-content/10 border-t">
      <button
        class="flex-1 btn btn-primary btn-sm"
        :class="{ loading: busy }"
        :disabled="busy"
        @click="submit"
      >
        Зберегти
      </button>
      <button
        class="btn btn-ghost btn-sm"
        @click="emit('close')"
      >
        Закрити
      </button>
    </div>
  </div>
</template>
